// File: src/hooks/useSpeechRecognition.ts

import { useEffect, useRef, useState } from "react";

type UseSpeechRecognitionOptions = {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
  onResult?: (transcript: string) => void;
  onEnd?: () => void;
};

export const useSpeechRecognition = ({
  lang = "en-US",
  continuous = true,
  interimResults = true,
  onResult,
  onEnd,
}: UseSpeechRecognitionOptions) => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const lastTranscriptRef = useRef("");

  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.lang = lang;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript.trim();
        if (event.results[i].isFinal) {
          finalTranscript += transcript + " ";
        }
      }

      if (finalTranscript && finalTranscript !== lastTranscriptRef.current) {
        lastTranscriptRef.current = finalTranscript;
        onResult?.(finalTranscript.trim());
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      lastTranscriptRef.current = "";
      onEnd?.();
    };

    recognitionRef.current = recognition;
  }, [lang, continuous, interimResults, onResult, onEnd]);

  const start = () => {
    recognitionRef.current?.start();
    setIsListening(true);
  };

  const stop = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
  };

  return { isListening, start, stop };
};